import { createContext, useContext } from 'react';
import cx from 'classnames';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import styles from '../List.module.scss';
import { ListItemProps, ListItemType, SortableItemContextType } from "../List.types";

const SortableItemContext = createContext<SortableItemContextType | undefined>(undefined);

/**
 * A sortable item component that integrates with the dnd-kit library for drag-and-drop functionality.
 * 
 * @template T - The type of the list item.
 * @param {ListItemProps<T>} props - The properties for the sortable item.
 * @returns {JSX.Element} The rendered sortable item.
 */
export const SortableItem = <T extends ListItemType>(props: ListItemProps<T>) => {
  const { id, component: Component, item, ...rest } = props;
  const { itemClassName, itemStyle, ...itemProps } = item;

  const {
    attributes,
    listeners,

    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <SortableItemContext.Provider value={{ id, transform, transition, attributes, listeners }}>
      <li
        {...rest}
        ref={setNodeRef}
        style={style}
        className={cx(styles.listItem, itemClassName)}
      >
        <Component
          {...itemProps}
        />
      </li>
    </SortableItemContext.Provider>
  );
}

export const useSortableItem = () => {
  const context = useContext(SortableItemContext);
  if (!context) {
    throw new Error('useSortableItem must be used within a SortableItem');
  }
  return context;
}
